import { Controller } from "@hotwired/stimulus"

// Controls the share modal in Plantafel
export default class extends Controller {
  static targets = [
    "modal",
    "bauleiterCheckbox",
    "employeeCheckbox",
    "externalEmailsInput",
    "externalEmailsHidden",
    "emailPillsContainer",
    "messageInput",
    "selectedCount",
    "submitButton",
    "submitText",
    "submitLoading",
    "employeeSection",
    "employeeSearch",
    "employeeChevron",
    "bauleiterExternalSection",
    "bauleiterExternalChevron",
    "bauleiterExternalInput",
    "bauleiterExternalHidden",
    "bauleiterEmailPillsContainer",
  ]

  connect() {
    // Lock body scroll when modal is open
    document.body.classList.add("overflow-hidden")
    // Initialize emails array for global external emails
    this.emails = []
    // Initialize per-bauleiter emails map
    this.bauleiterEmails = {}
    this.updateSelectedCount()
  }

  disconnect() {
    // Unlock body scroll when modal closes
    document.body.classList.remove("overflow-hidden")
  }

  close() {
    // Remove modal from DOM via Turbo
    this.element.remove()
  }

  toggleEmployeeSection(event) {
    event.preventDefault()

    if (this.hasEmployeeSectionTarget) {
      this.employeeSectionTarget.classList.toggle("hidden")
    }

    if (this.hasEmployeeChevronTarget) {
      this.employeeChevronTarget.classList.toggle("rotate-90")
    }
  }

  searchEmployees(event) {
    const query = event.target.value.toLowerCase()

    if (!this.hasEmployeeCheckboxTarget) return

    this.employeeCheckboxTargets.forEach((checkbox) => {
      const label = checkbox.closest("label")
      const nameAttr = label.getAttribute("data-employee-name")

      if (query === "" || (nameAttr && nameAttr.includes(query))) {
        label.classList.remove("hidden")
      } else {
        label.classList.add("hidden")
      }
    })
  }

  // Focus the email input when clicking the container
  focusEmailInput() {
    if (this.hasExternalEmailsInputTarget) {
      this.externalEmailsInputTarget.focus()
    }
  }

  // Handle keydown in email input
  handleEmailKeydown(event) {
    const input = event.target
    const value = input.value.trim()

    // Create pill on Enter, comma, semicolon, or Tab
    if (["Enter", ",", ";", "Tab"].includes(event.key)) {
      if (value) {
        event.preventDefault()
        this.addEmail(value)
        input.value = ""
      } else if (event.key === "Tab") {
        // Allow tab to move focus if no value
        return
      } else {
        event.preventDefault()
      }
    }

    // Remove last pill on Backspace if input is empty
    if (event.key === "Backspace" && !value && this.emails.length > 0) {
      this.removeEmail(this.emails.length - 1)
    }
  }

  // Handle blur - add email if valid
  handleEmailBlur(event) {
    const value = event.target.value.trim()
    if (value && this.isValidEmail(value)) {
      this.addEmail(value)
      event.target.value = ""
    }
  }

  // Add an email and create pill
  addEmail(email) {
    email = email.trim().replace(/[,;]/g, "")

    if (!email || !this.isValidEmail(email)) {
      return
    }

    // Check for duplicates
    if (this.emails.includes(email.toLowerCase())) {
      return
    }

    this.emails.push(email.toLowerCase())
    this.renderPill(email)
    this.updateHiddenInput()
    this.updateSelectedCount()
    this.updatePlaceholder()
  }

  // Remove email by index
  removeEmail(index) {
    this.emails.splice(index, 1)
    this.renderAllPills()
    this.updateHiddenInput()
    this.updateSelectedCount()
    this.updatePlaceholder()
  }

  // Validate email format
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  // Render a single pill
  renderPill(email) {
    if (!this.hasEmailPillsContainerTarget || !this.hasExternalEmailsInputTarget) return

    const pill = document.createElement("span")
    pill.className =
      "inline-flex items-center gap-1 px-2 py-1 text-sm bg-gray-100 text-gray-800 rounded-md"
    pill.dataset.email = email
    pill.innerHTML = `
      <span>${this.escapeHtml(email)}</span>
      <button type="button"
              class="text-gray-500 hover:text-gray-700 focus:outline-hidden"
              data-action="click->share-recipients-modal#removeEmailPill">
        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `

    // Insert pill before the input
    this.emailPillsContainerTarget.insertBefore(pill, this.externalEmailsInputTarget)
  }

  // Re-render all pills (used after removal)
  renderAllPills() {
    if (!this.hasEmailPillsContainerTarget || !this.hasExternalEmailsInputTarget) return

    // Remove all existing pills
    const existingPills = this.emailPillsContainerTarget.querySelectorAll("span[data-email]")
    existingPills.forEach((pill) => pill.remove())

    // Re-render all
    this.emails.forEach((email) => this.renderPill(email))
  }

  // Handle click on pill remove button
  removeEmailPill(event) {
    event.preventDefault()
    event.stopPropagation()

    const pill = event.target.closest("span[data-email]")
    if (pill) {
      const email = pill.dataset.email
      const index = this.emails.indexOf(email)
      if (index > -1) {
        this.removeEmail(index)
      }
    }
  }

  // Update hidden input with comma-separated emails
  updateHiddenInput() {
    if (this.hasExternalEmailsHiddenTarget) {
      this.externalEmailsHiddenTarget.value = this.emails.join(", ")
    }
  }

  // Update placeholder based on whether there are emails
  updatePlaceholder() {
    if (this.hasExternalEmailsInputTarget) {
      if (this.emails.length > 0) {
        this.externalEmailsInputTarget.placeholder = ""
      } else {
        this.externalEmailsInputTarget.placeholder =
          this.externalEmailsInputTarget.dataset.placeholder || "name@beispiel.de"
      }
    }
  }

  // Escape HTML to prevent XSS
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  updateSelectedCount() {
    let count = 0

    // Count checked bauleiters
    if (this.hasBauleiterCheckboxTarget) {
      count += this.bauleiterCheckboxTargets.filter((cb) => cb.checked && !cb.disabled).length
    }

    // Count checked employees
    if (this.hasEmployeeCheckboxTarget) {
      count += this.employeeCheckboxTargets.filter((cb) => cb.checked && !cb.disabled).length
    }

    // Count global external emails
    count += this.emails.length

    // Count per-bauleiter external emails
    Object.values(this.bauleiterEmails).forEach((emails) => {
      count += emails.length
    })

    // Update UI
    if (this.hasSelectedCountTarget) {
      this.selectedCountTarget.textContent = count
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = count === 0
    }
  }

  submit(_event) {
    // Add any remaining text in global input as email before submit
    if (this.hasExternalEmailsInputTarget) {
      const value = this.externalEmailsInputTarget.value.trim()
      if (value && this.isValidEmail(value)) {
        this.addEmail(value)
        this.externalEmailsInputTarget.value = ""
      }
    }

    // Add any remaining text in per-bauleiter inputs as emails before submit
    if (this.hasBauleiterExternalInputTarget) {
      this.bauleiterExternalInputTargets.forEach((input) => {
        const value = input.value.trim()
        const baleiterId = input.dataset.baleiterId
        if (value && this.isValidEmail(value)) {
          this.addBauleiterEmail(baleiterId, value)
          input.value = ""
        }
      })
    }

    // Show loading state
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }

    if (this.hasSubmitTextTarget) {
      this.submitTextTarget.classList.add("hidden")
    }

    if (this.hasSubmitLoadingTarget) {
      this.submitLoadingTarget.classList.remove("hidden")
    }

    // Form will be submitted via Turbo, which will handle the response
  }

  // ==========================================
  // Per-Bauleiter External Email Methods
  // ==========================================

  toggleBauleiterExternals(event) {
    event.preventDefault()
    const bauleiterId = event.currentTarget.dataset.bauleiterId

    // Find the section for this bauleiter
    const section = this.bauleiterExternalSectionTargets.find(
      (s) => s.dataset.bauleiterId === bauleiterId,
    )

    // Find the chevron for this bauleiter
    const chevron = this.bauleiterExternalChevronTargets.find(
      (c) => c.dataset.bauleiterId === bauleiterId,
    )

    if (section) {
      section.classList.toggle("hidden")
    }

    if (chevron) {
      // Toggle between plus and minus icon
      if (section && !section.classList.contains("hidden")) {
        chevron.innerHTML =
          '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 12H4" />'
      } else {
        chevron.innerHTML =
          '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />'
      }
    }
  }

  focusBauleiterEmailInput(event) {
    const bauleiterId = event.currentTarget.dataset.bauleiterId
    const input = this.bauleiterExternalInputTargets.find(
      (i) => i.dataset.bauleiterId === bauleiterId,
    )
    if (input) {
      input.focus()
    }
  }

  handleBauleiterEmailKeydown(event) {
    const input = event.target
    const value = input.value.trim()
    const bauleiterId = input.dataset.bauleiterId

    // Create pill on Enter, comma, semicolon, or Tab
    if (["Enter", ",", ";", "Tab"].includes(event.key)) {
      if (value) {
        event.preventDefault()
        this.addBauleiterEmail(bauleiterId, value)
        input.value = ""
      } else if (event.key === "Tab") {
        return
      } else {
        event.preventDefault()
      }
    }

    // Remove last pill on Backspace if input is empty
    const emails = this.bauleiterEmails[bauleiterId] || []
    if (event.key === "Backspace" && !value && emails.length > 0) {
      this.removeBauleiterEmail(bauleiterId, emails.length - 1)
    }
  }

  handleBauleiterEmailBlur(event) {
    const value = event.target.value.trim()
    const bauleiterId = event.target.dataset.bauleiterId
    if (value && this.isValidEmail(value)) {
      this.addBauleiterEmail(bauleiterId, value)
      event.target.value = ""
    }
  }

  addBauleiterEmail(bauleiterId, email) {
    email = email.trim().replace(/[,;]/g, "")

    if (!email || !this.isValidEmail(email)) {
      return
    }

    // Initialize array if needed
    if (!this.bauleiterEmails[bauleiterId]) {
      this.bauleiterEmails[bauleiterId] = []
    }

    // Check for duplicates
    if (this.bauleiterEmails[bauleiterId].includes(email.toLowerCase())) {
      return
    }

    this.bauleiterEmails[bauleiterId].push(email.toLowerCase())
    this.renderBauleiterPill(bauleiterId, email)
    this.updateBauleiterHiddenInput(bauleiterId)
    this.updateSelectedCount()
  }

  removeBauleiterEmail(bauleiterId, index) {
    if (!this.bauleiterEmails[bauleiterId]) return

    this.bauleiterEmails[bauleiterId].splice(index, 1)
    this.renderAllBauleiterPills(bauleiterId)
    this.updateBauleiterHiddenInput(bauleiterId)
    this.updateSelectedCount()
  }

  renderBauleiterPill(bauleiterId, email) {
    const container = this.bauleiterEmailPillsContainerTargets.find(
      (c) => c.dataset.bauleiterId === bauleiterId,
    )
    const input = this.bauleiterExternalInputTargets.find(
      (i) => i.dataset.bauleiterId === bauleiterId,
    )

    if (!container || !input) return

    const pill = document.createElement("span")
    pill.className =
      "inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-gray-100 text-gray-800 rounded-md"
    pill.dataset.email = email
    pill.dataset.bauleiterId = bauleiterId
    pill.innerHTML = `
      <span>${this.escapeHtml(email)}</span>
      <button type="button"
              class="text-gray-500 hover:text-gray-700 focus:outline-hidden"
              data-action="click->share-recipients-modal#removeBauleiterEmailPill">
        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    `

    container.insertBefore(pill, input)
  }

  renderAllBauleiterPills(bauleiterId) {
    const container = this.bauleiterEmailPillsContainerTargets.find(
      (c) => c.dataset.bauleiterId === bauleiterId,
    )

    if (!container) return

    // Remove existing pills
    const existingPills = container.querySelectorAll("span[data-email]")
    existingPills.forEach((pill) => pill.remove())

    // Re-render all pills
    const emails = this.bauleiterEmails[bauleiterId] || []
    emails.forEach((email) => this.renderBauleiterPill(bauleiterId, email))
  }

  removeBauleiterEmailPill(event) {
    event.preventDefault()
    event.stopPropagation()

    const pill = event.target.closest("span[data-email]")
    if (pill) {
      const email = pill.dataset.email
      const bauleiterId = pill.dataset.bauleiterId
      const emails = this.bauleiterEmails[bauleiterId] || []
      const index = emails.indexOf(email)
      if (index > -1) {
        this.removeBauleiterEmail(bauleiterId, index)
      }
    }
  }

  updateBauleiterHiddenInput(bauleiterId) {
    const hidden = this.bauleiterExternalHiddenTargets.find(
      (h) => h.dataset.bauleiterId === bauleiterId,
    )
    if (hidden) {
      const emails = this.bauleiterEmails[bauleiterId] || []
      hidden.value = emails.join(", ")
    }
  }
}
